import { Box, Button, Checkbox, Stack, SxProps, Theme } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";

import { IFlowStepProps } from "../../../Flows/types/IFlow";
import { IStepWrapperButtonProps } from "../../Components/StepWrapper";
import StepWrapper from "../../Components/StepWrapper";
import IMedicalConclusions from "../types/IMedicalConclusions";
import IMedicalQualification from "../types/IMedicalQualification";
import { IMedicalStepConfig, IMedicalStepInnerContentProps, IMedicalStepProps } from "./FormSeriesMedicalStep";

export const classNames = {
  noToAllButton: "medicalStep-noToAllButton"
};

export interface INoToAllFormSeriesMedicalStepDecoratorConfig {
  /** Renders the inner medical step with no-to-all behavior applied to the configured questions. */
  InnerForm: React.FC<IMedicalStepInnerContentProps>;
  /** The medical question ids to reset to false for a no-to-all action. */
  rootMedicalQuestionIds: string[];
  /** The nested/conditional medical question ids to reset to null for a no-to-all action. */
  nestedMedicalQuestionIds: string[];
}

const resetNoToAllMedicalFormState = (
  oldState: IMedicalQualification,
  rootMedicalQuestionIds: string[],
  nestedMedicalQuestionIds: string[]
): IMedicalQualification => {
  const resetFormState = { ...oldState };
  rootMedicalQuestionIds.forEach(questionId => {
    resetFormState[questionId] = false;
  });
  nestedMedicalQuestionIds.forEach(questionId => {
    resetFormState[questionId] = null;
  });
  return resetFormState;
};

const resetNoToAllMedicalConclusions = (oldState: IMedicalConclusions): IMedicalConclusions => ({
  ...oldState,
  hasRadiationProtectedAreas: false,
  hasPacemakerWithoutDefibrillator: null,
  hasDiabetes: false
});

interface INoToAllButtonProps {
  onClick: () => void;
  checked: boolean;
  sx?: SxProps<Theme>;
}

const NoToAllButton: React.FC<INoToAllButtonProps> = ({ onClick, checked, sx }) => {
  return (
    <Box alignItems="center" display="flex" justifyContent="flex-end" width="100%" sx={{ ...sx }}>
      <Button
        className={classNames.noToAllButton}
        type="button"
        onClick={onClick}
        sx={{
          color: "common.white",
          "& .MuiButton-endIcon": {
            color: "inherit"
          }
        }}
        endIcon={
          <Checkbox
            checked={checked}
            disableRipple
            sx={{
              p: 0,
              pointerEvents: "none",
              color: "inherit",
              "&.Mui-checked": { color: "inherit" }
            }}
            tabIndex={-1}
          />
        }
      >
        No to All
      </Button>
    </Box>
  );
};

/** Decorates the shared form-series medical step by injecting top/bottom No-to-All controls into the form question stream. */
const NoToAllFormSeriesMedicalStepDecorator: React.FC<
  IMedicalStepProps & IMedicalStepConfig & IStepWrapperButtonProps & INoToAllFormSeriesMedicalStepDecoratorConfig
> = ({
  InnerForm,
  FormQuestions,
  rootMedicalQuestionIds,
  nestedMedicalQuestionIds,
  medicalQualificationEvaluator,
  nextStep,
  router,
  onMedicalNoncandidateDisqualification,
  useCreateNonCandidateContactResult,
  NextButtonMobile,
  NextButtonDesktop,
  ...props
}) => {
  const flowStepProps = useOutletContext<IFlowStepProps>();
  const contentBottomAnchorRef = useRef<HTMLDivElement | null>(null);
  const { execute: createNonCandidateContactResult, result, isLoading } = useCreateNonCandidateContactResult();

  useEffect(() => {
    if (result) router.goToNonCandidateView();
  }, [result]);

  const handleNext = () => {
    const isNonCandidate = medicalQualificationEvaluator.isNonCandidate(props.value);
    if (isNonCandidate) {
      onMedicalNoncandidateDisqualification();
      createNonCandidateContactResult();
    } else flowStepProps.setStep(nextStep);
  };

  const onNoToAll = () => {
    props.setMedicalFormState(oldState =>
      resetNoToAllMedicalFormState(oldState, rootMedicalQuestionIds, nestedMedicalQuestionIds)
    );
    props.setMedicalConclusions(resetNoToAllMedicalConclusions);
  };

  const onNoToAllWithScroll = () => {
    onNoToAll();
    requestAnimationFrame(() => {
      contentBottomAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    });
  };

  const isNoToAllChecked =
    rootMedicalQuestionIds.length > 0 && rootMedicalQuestionIds.every(questionId => props.value[questionId] === false);

  return (
    <StepWrapper
      NextButtonMobile={NextButtonMobile}
      NextButtonDesktop={NextButtonDesktop}
      nextButtonLoading={isLoading}
      onNext={handleNext}
      contentBottomAnchorRef={contentBottomAnchorRef}
      contentBottomAnchorHeight="36px"
      disabled={isLoading || !medicalQualificationEvaluator.isComplete(props.value)}
    >
      <Stack sx={{ pt: 2, pb: "36px" }}>
        <Stack spacing={0} width="100%">
          <NoToAllButton checked={isNoToAllChecked} onClick={onNoToAllWithScroll} sx={{ pb: 2 }} />
          <InnerForm
            value={props.value}
            setMedicalConclusions={props.setMedicalConclusions}
            setMedicalFormState={props.setMedicalFormState}
            FormQuestions={FormQuestions}
          />
          <NoToAllButton checked={isNoToAllChecked} onClick={onNoToAll} />
        </Stack>
      </Stack>
    </StepWrapper>
  );
};

export default NoToAllFormSeriesMedicalStepDecorator;
